import React, { createContext, useEffect, useState, useCallback } from "react";
import { showToast } from "../components/ToastNotification";
import authService from "../services/authService";
import analytics from "../services/analytics";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);
  const [sessionWarning, setSessionWarning] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Handle session expiration warning
  const handleExpirationWarning = useCallback((timeRemaining) => {
    if (!sessionWarning) {
      setSessionWarning(true);
      const minutes = Math.floor(timeRemaining / 60000);
      showToast(`Your session will expire in ${minutes} minutes`, "warning");
    }
  }, [sessionWarning]);

  // Handle logout with reason
  const handleLogout = useCallback((reason) => {
    setIsAuthenticated(false);
    setUserData(null);
    setSessionWarning(false);
    setIsLoading(false);
    
    // Show appropriate message based on reason
    switch (reason) {
      case 'token_expired':
        showToast("Your session has expired. Please log in again.", "info");
        break;
      case 'invalid_token':
        showToast("Invalid session detected. Please log in again.", "error");
        break;
      case 'inactivity':
        showToast("Logged out due to inactivity.", "info");
        break;
      case 'cross_tab_logout':
        showToast("You have been logged out from another tab.", "info");
        break;
      default:
        showToast("Logged out successfully");
    }
  }, []);

  // Initialize auth service
  useEffect(() => {
    authService.init({
      onExpirationWarning: handleExpirationWarning,
      onLogout: handleLogout
    });
    authService.enableCrossTabSync();
  }, [handleExpirationWarning, handleLogout]);

  // Check current token validity
  const checkTokenValidity = () => {
    const authState = authService.validateAuth();
    if (!authState.isValid) {
      authService.logout();
      setIsAuthenticated(false);
      setUserData(null);
      return false;
    }
    return true;
  };

  // Auto-restore session on mount
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const authState = authService.validateAuth();
        
        if (authState.isValid) {
          const userData = authService.getUserData();
          setIsAuthenticated(true);
          setUserData(userData);
          authService.startTokenMonitoring();
        } else {
          authService.logout();
          setIsAuthenticated(false);
          setUserData(null);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    restoreSession();
  }, []);

  // Check token validity on window focus/visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated) {
        checkTokenValidity();
      }
    };

    const handleFocus = () => {
      if (isAuthenticated) {
        checkTokenValidity();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated]);

  const login = (accessToken, refreshToken, rememberMe, userId, userData) => {
    // Use centralized auth service for secure token storage
    authService.login(accessToken, refreshToken, userId, userData, rememberMe);
    
    setIsAuthenticated(true);
    setUserData(userData);
    setSessionWarning(false);
    setIsLoading(false);
    showToast("Logged in successfully");
  };

  const logout = () => {
    // Track logout event before clearing data
    analytics.trackLogout();
    
    // Use centralized auth service for proper cleanup
    authService.logout();
    
    setIsAuthenticated(false);
    setUserData(null);
    setSessionWarning(false);
    showToast("Logged out successfully");
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, userData, login, logout, checkTokenValidity, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};
